import React, { useEffect, useState } from "react";
import { Video, Loader2, Trash2, Play, ExternalLink, CheckCircle2, Link as LinkIcon, Camera } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import VideoRecorder from "@/components/VideoRecorder";

// -----------------------------------------------------------------------------
// VideoIntroEditor — reusable card for matrimonial / dating profile video.
//   props: kind = "dating" | "matrimonial"
//   Fetches current video, lets user paste any https:// URL (YouTube, Vimeo,
//   direct MP4 or Emergent Object Storage), preview it, or clear it.
// -----------------------------------------------------------------------------
export default function VideoIntroEditor({ kind = "dating" }) {
  const [state, setState] = useState({ loading: true, has_profile: false });
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("record");            // "record" | "url"

  const load = async () => {
    try {
      const r = await api.get("/profile/video-intro", { params: { kind } });
      setState({ loading: false, ...r.data });
      setUrl(r.data.video_intro_url || "");
      setCaption(r.data.video_intro_caption || "");
    } catch { setState({ loading: false, has_profile: false }); }
  };
  useEffect(() => { load(); }, [kind]);   // eslint-disable-line

  const save = async () => {
    if (!/^https?:\/\/\S+$/.test(url.trim())) { toast.error("Paste a full https:// video URL"); return; }
    setBusy(true);
    try {
      await api.patch("/profile/video-intro", { kind, video_url: url.trim(), caption: caption.trim() || undefined });
      toast.success("Video intro saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove your video intro?")) return;
    setBusy(true);
    try { await api.delete("/profile/video-intro", { params: { kind } }); toast.success("Removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };

  if (state.loading) return <div className="rounded-3xl bg-white border border-stone-200 p-6 text-center"><Loader2 className="animate-spin text-stone-400 mx-auto"/></div>;

  const current = state.video_intro_url;
  const label = kind === "matrimonial" ? "Matrimonial" : "Dating";

  return (
    <div className="rounded-3xl bg-white border border-stone-200 p-4 space-y-3" data-testid={`video-intro-${kind}`}>
      <div className="flex items-center gap-2">
        <Video size={16} className="text-fuchsia-600"/>
        <h3 className="font-black text-stone-900">{label} — Video intro</h3>
        {current && <span className="text-[10px] font-black uppercase rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 size={10}/> active</span>}
      </div>
      <p className="text-xs text-stone-500">A short 30-60 s video helps matches trust you're real. Paste any public URL — YouTube, Vimeo, or a direct MP4.</p>

      {current && (
        <div className="rounded-2xl overflow-hidden bg-black relative aspect-video border border-stone-200" data-testid={`video-intro-preview-${kind}`}>
          {/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/i.test(current) ? (
            <iframe
              title="Video intro"
              className="w-full h-full"
              src={current.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").split("&")[0]}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video src={current} controls className="w-full h-full object-contain bg-black" preload="metadata"/>
          )}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex gap-1" data-testid={`video-intro-mode-${kind}`}>
          <button
            onClick={() => setMode("record")}
            className={`text-[11px] font-black rounded-full px-3 py-1 inline-flex items-center gap-1 border ${mode === "record" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
            data-testid={`video-intro-mode-record-${kind}`}
          >
            <Camera size={11}/> Record now
          </button>
          <button
            onClick={() => setMode("url")}
            className={`text-[11px] font-black rounded-full px-3 py-1 inline-flex items-center gap-1 border ${mode === "url" ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
            data-testid={`video-intro-mode-url-${kind}`}
          >
            <LinkIcon size={11}/> Paste URL
          </button>
        </div>

        {mode === "record" ? (
          <VideoRecorder
            maxSeconds={60}
            onDone={async (publicUrl) => {
              setUrl(publicUrl);
              setBusy(true);
              try {
                await api.patch("/profile/video-intro", { kind, video_url: publicUrl, caption: caption.trim() || undefined });
                toast.success("Video intro saved");
                load();
              } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
              finally { setBusy(false); }
            }}
          />
        ) : (
          <Input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://youtube.com/watch?v=… or https://…/intro.mp4"
            className="rounded-full text-sm"
            data-testid={`video-intro-url-${kind}`}
          />
        )}
        <Input
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="Optional caption (max 200 chars)"
          maxLength={200}
          className="rounded-full text-sm"
          data-testid={`video-intro-caption-${kind}`}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {mode === "url" && (
          <Button
            onClick={save} disabled={busy}
            className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-black h-9"
            data-testid={`video-intro-save-${kind}`}
          >
            {busy ? <Loader2 size={13} className="animate-spin mr-1"/> : <Play size={13} className="mr-1"/>}
            {current ? "Update" : "Attach"} video
          </Button>
        )}
        {current && (
          <Button
            onClick={remove} disabled={busy} variant="outline"
            className="rounded-full h-9 border-stone-300"
            data-testid={`video-intro-delete-${kind}`}
          >
            <Trash2 size={13} className="mr-1"/> Remove
          </Button>
        )}
        {current && (
          <a href={current} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-stone-500 hover:text-stone-900 py-2">
            Open <ExternalLink size={11}/>
          </a>
        )}
      </div>

      {!state.has_profile && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl p-2">
          You don't have a {label.toLowerCase()} profile yet — saving a video now creates a draft profile. You can complete the rest later from <a className="underline font-bold" href={`/post?category=${kind}`}>Post → {label}</a>.
        </div>
      )}
    </div>
  );
}
